// Dialog zum Anlegen/Bearbeiten einer Anmeldetext-Vorlage (Rich-Text-Umstellung, Teil 3)
//
// Wortgleiche Übertragung von PaymentNoteTemplateFormDialog.tsx auf den anderen
// Vorlagentyp. Siehe dort für die volle Begründung jeder Zusicherung.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/features/events/RichTextEditor';
import { isBlankMarkdown } from '@/features/events/event-text-pick';
import type { RegistrationNoteTemplate, SaveRegistrationNoteTemplateRequest } from '@/types';

interface Props {
  open: boolean;
  /** null = anlegen; sonst bearbeiten. */
  template: RegistrationNoteTemplate | null;
  /** Serverfehler (409 der Sperre, 404): erscheint im Dialog, nicht als Toast. */
  saveError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSave: (req: SaveRegistrationNoteTemplateRequest) => void;
  isSaving?: boolean;
}

export interface RegistrationNoteTemplateFormValues {
  title: string;
  text: string;
}

const emptyForm = (): RegistrationNoteTemplateFormValues => ({ title: '', text: '' });

/** Die Fehler, die diese Prüfung melden kann — als i18n-Schlüssel. */
export type RegistrationNoteTemplateFormError =
  | 'registrationNoteTemplates.form.errors.title'
  | 'registrationNoteTemplates.form.errors.text';

/** Prüft das Formular und gibt einen i18n-Schlüssel zurück, oder null. */
export function validateRegistrationNoteTemplateForm(
  values: RegistrationNoteTemplateFormValues
): RegistrationNoteTemplateFormError | null {
  if (values.title.trim().length === 0) return 'registrationNoteTemplates.form.errors.title';
  if (isBlankMarkdown(values.text)) return 'registrationNoteTemplates.form.errors.text';

  return null;
}

export function RegistrationNoteTemplateFormDialog({
  open,
  template,
  saveError,
  onOpenChange,
  onSave,
  isSaving,
}: Props) {
  const { t } = useTranslation('settings');
  const [form, setForm] = useState<RegistrationNoteTemplateFormValues>(emptyForm);
  const [error, setError] = useState<RegistrationNoteTemplateFormError | null>(null);

  const isEdit = template !== null;

  useEffect(() => {
    if (!open) return;
    setForm(template ? { title: template.title, text: template.text } : emptyForm());
    setError(null);
  }, [open, template]);

  const handleSubmit = () => {
    const problem = validateRegistrationNoteTemplateForm(form);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    onSave({
      id: template?.id,
      title: form.title.trim(),
      text: form.text,
      etag: template?.etag,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              isEdit
                ? 'registrationNoteTemplates.form.titleEdit'
                : 'registrationNoteTemplates.form.titleCreate'
            )}
          </DialogTitle>
          <DialogDescription>{t('registrationNoteTemplates.form.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label
              className="text-xs text-muted-foreground"
              htmlFor="registration-note-template-title"
            >
              {t('registrationNoteTemplates.form.fields.title')}
            </label>
            <Input
              id="registration-note-template-title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Telefonisch"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {t('registrationNoteTemplates.form.fields.text')}
            </label>
            <RichTextEditor
              value={form.text}
              onChange={(markdown) => setForm((prev) => ({ ...prev, text: markdown }))}
            />
          </div>

          {error && <p className="text-sm text-red-600">{t(error)}</p>}
          {!error && saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('registrationNoteTemplates.form.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving
              ? t('registrationNoteTemplates.form.saving')
              : t('registrationNoteTemplates.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
